import React, { FC, useEffect, useState } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { QcsButton } from '../common/basic/QcsButton';
import { TableRowButton } from '../common/basic/TableRowButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { StyledQcsTable } from '../common/grid/Grid';
import {
  getTrainingVersion,
  selectTrainingVersion,
  selectTrainingVersionLanguages,
  selectTrainingVersionState,
} from '../../store/entities/trainingVersion';
import { AddLanguageModal } from './AddLanguageModal';
import { FetchState } from '../../store/fetchState';
import { QcsTableBody } from '../common/basic/QcsTableBody';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTableHead } from '../common/basic/QcsTableHead';
import { QcsTableRow } from '../common/basic/QcsTableRow';
import { QcsPaper } from '../common/basic/QcsPaper';
import { QcsTableContainer } from '../common/basic/QcsTableContainer';
import { GridEmpty } from '../common/grid/GridEmpty';
import { GridError } from '../common/grid/GridError';
import { GridLoading } from '../common/grid/GridLoading';
import { FaIcon } from '../common/FaIcon';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { LanguageDto } from '@api/models/settings/LanguageDto';
import { getLanguageName } from '../../utils/i18n';
import { PageType } from '@api/models/enums/PageType';
import { companyService, questionnaireVersionService, trainingVersionService } from '@api/services/services';

const HeaderContainer = styled.div((): CSSObject => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '1rem',
  marginBottom: '0.5rem',
}));

export const TrainingSlideLanguages: FC = () => {
  const { t } = useTranslation();
  const trainingVersionLanguages = useAppSelector(selectTrainingVersionLanguages);
  const trainingVersionState = useAppSelector(selectTrainingVersionState);
  const trainingVersion = useAppSelector(selectTrainingVersion);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { trainingId, trainingVersionId } = useParams();
  const [adding, setAdding] = useState(false);
  const [languageToCopy, setLanguageToCopy] = useState<LanguageDto | null>(null);
  const [afterTrainingLanguages, setAfterTrainingLanguages] = useState<string[]>();
  const [questionnaireLanguages, setQuestionnaireLanguages] = useState<string[]>();
  const questionnaireId = trainingVersion.questionnaire?.id;
  const trainingValidFrom = trainingVersion.validFrom;

  useEffect(() => {
    const loadAfterTrainingLanguages = async () => {
      const res = await companyService.queryCompanyPages({ type: PageType.AfterTraining });
      setAfterTrainingLanguages(res.data.filter((page) => page.content).map((page) => page.language.toLowerCase()));
    };
    loadAfterTrainingLanguages();
  }, []);

  useEffect(() => {
    if (!questionnaireId) return;
    const loadQuestionnaireLanguages = async () => {
      const res = await questionnaireVersionService.query({ questionnaireId, getAll: true });
      // Pick the questionnaire version that is valid when this training version becomes valid,
      // i.e. validFrom <= trainingValidFrom <= validTo (validTo is open-ended on the last version).
      const questionnaireVersion = (res.data.content ?? []).find(
        (version) => version.validFrom <= trainingValidFrom && (!version.validTo || trainingValidFrom <= version.validTo),
      );
      setQuestionnaireLanguages(questionnaireVersion?.languages?.map((language) => language.code.toLowerCase()) ?? []);
    };
    loadQuestionnaireLanguages();
  }, [questionnaireId, trainingValidFrom]);

  const loadTrainingVersion = () => {
    dispatch(getTrainingVersion(trainingVersionId!));
  };

  const handleRowClick = (item: LanguageDto) => () => {
    navigate(`/training/${trainingId}/${trainingVersionId}/${item.code}`);
  };

  const handleAddClick = () => {
    setAdding(true);
  };

  const handleCloseAdding = () => {
    setAdding(false);
    setLanguageToCopy(null);
  };

  const handleAddVersionCopy = (language: LanguageDto) => () => {
    setLanguageToCopy(language);
  };

  const deleteLanguage = (languageCode: string) => async () => {
    await trainingVersionService.deleteTVLanguageVariant(trainingVersionId!, languageCode);
    loadTrainingVersion();
  };

  if (!trainingVersionLanguages) {
    return null;
  }

  return (
    <>
      <AddLanguageModal
        open={adding || !!languageToCopy}
        languageToCopy={languageToCopy}
        onClose={handleCloseAdding}
        reloadTrainingVersion={loadTrainingVersion}
      />
      <HeaderContainer>
        <QcsTypography variant="h5">{t('training.slides')}</QcsTypography>
        <QcsButton disabled={!trainingVersion.editable} variant="contained" onClick={handleAddClick}>
          {t('training.addLanguage')}
        </QcsButton>
      </HeaderContainer>

      <QcsPaper>
        <QcsTableContainer>
          <StyledQcsTable>
            <QcsTableHead>
              <QcsTableRow>
                <QcsTableCell>{t('training.language')}</QcsTableCell>
                <QcsTableCell>{t('training.slidesCount')}</QcsTableCell>
                <QcsTableCell align="center" sx={{ width: '15rem' }}>
                  {t('training.copy.addLanguage')}
                </QcsTableCell>
                <QcsTableCell align="center" sx={{ width: '15rem' }}>
                  {t('training.deleteLanguage')}
                </QcsTableCell>
              </QcsTableRow>
            </QcsTableHead>
            <QcsTableBody>
              {trainingVersionState === FetchState.Loaded &&
                trainingVersionLanguages.map((item, index) => (
                  <QcsTableRow key={index} hover={true} onClick={handleRowClick(item.language)}>
                    <QcsTableCell>
                      <img src={'/flags/' + item.language.flag + '.svg'} height="20" /> {getLanguageName(t, item.language)}
                      {afterTrainingLanguages && !afterTrainingLanguages.includes(item.language.code.toLowerCase()) && (
                        <QcsTypography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                          {t('training.missingAfterTrainingPage', { language: getLanguageName(t, item.language) })}
                        </QcsTypography>
                      )}
                      {questionnaireId && questionnaireLanguages && !questionnaireLanguages.includes(item.language.code.toLowerCase()) && (
                        <QcsTypography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                          {t('training.missingQuestionnaireLanguage', { language: getLanguageName(t, item.language) })}
                        </QcsTypography>
                      )}
                    </QcsTableCell>
                    <QcsTableCell>
                      <QcsTypography>{item.slides}</QcsTypography>
                    </QcsTableCell>
                    <QcsTableCell align="center">
                      <TableRowButton disabled={!trainingVersion.editable} onClick={handleAddVersionCopy(item.language)}>
                        <FaIcon icon={faCopy} />
                      </TableRowButton>
                    </QcsTableCell>
                    <QcsTableCell align="center">
                      <TableRowButton disabled={!trainingVersion.editable} onClick={deleteLanguage(item.language.code)}>
                        <DeleteForeverIcon color={!trainingVersion.editable ? undefined : 'error'} />
                      </TableRowButton>
                    </QcsTableCell>
                  </QcsTableRow>
                ))}
            </QcsTableBody>
          </StyledQcsTable>
          {trainingVersionState === FetchState.Loading ? (
            <GridLoading />
          ) : trainingVersionState === FetchState.Error ? (
            <GridError />
          ) : trainingVersionLanguages.length === 0 ? (
            <GridEmpty />
          ) : null}
        </QcsTableContainer>
      </QcsPaper>
    </>
  );
};
